import type { Position } from './positionTypes'
import { PositionType, PositionOpenStatus, PositionResult } from './positionTypes'
import { FormStatus } from './formTypes'
import { AuditType, type FullAudit } from './auditTypes'
import {
  createImpactModelSnapshotV1,
  type ImpactModelInputsV1,
  type ImpactModelStageV1,
} from '~/utils/impactModelV1'

export const exampleAIEngineerAudit: FullAudit = {
  id: 'example-ai-engineer-audit',
  orgId: 'example-org',
  status: FormStatus.Complete,
  archived: false,
  auditName: 'Example Audit',
  auditType: AuditType.Full,
  createdAt: '2026-04-26T12:00:00Z',
  updatedAt: '2026-04-26T12:00:00Z',
  auditData: {
    hiringProcessFrequency: 'often',
    screeningEvaluationFrequency: 'often',
    matchingRolesFrequency: 'often',
    candidateExperienceFrequency: 'often',
  },
}

const exampleAIEngineerPosition: Position = {
  id: 'example-ai-engineer',
  orgId: 'example-org',
  positionName: 'Example - AI Engineer',
  positionType: PositionType.FullTime,
  status: FormStatus.Complete,
  openStatus: PositionOpenStatus.Open,
  result: PositionResult.Unfilled,
  createdBy: 'system',
  createdAt: 'Apr 26, 2026',
  updatedAt: 'Apr 26, 2026',
  isExample: true,
  positionData: {
    jobDescription: `# Senior AI Engineer (Forecasting)

## About the Role
Fernway is seeking a Senior AI Engineer to design, build, and scale machine learning systems that power electricity demand and renewable energy forecasting for utility customers.
You will work on real-world, high-impact forecasting problems at the intersection of machine learning, energy systems, and climate infrastructure. Your models will directly influence how utilities dispatch energy, integrate renewables, and respond to extreme weather events.
This is a deeply technical, production-focused ML role — not an application-layer or generic AI engineering position. You will own models end-to-end and help define the next generation of forecasting systems used by grid operators.

## Key Responsibilities
- Own the architecture and development of production ML models for electricity demand, solar, wind, and grid stress forecasting
- Design, build, and maintain scalable ML pipelines, training systems, and evaluation frameworks
- Deploy and monitor production models serving real-time predictions for utility customers
- Lead technical rollout of models for new customers, including data integration, tuning, and performance validation
- Drive experimentation in probabilistic forecasting methods (e.g., quantile regression, conformal prediction, Bayesian approaches)
- Build and improve MLOps infrastructure including retraining pipelines, monitoring, and model evaluation systems
- Collaborate closely with customer engineering, product, and research teams to translate utility needs into ML solutions
- Communicate model behavior, limitations, and failure modes clearly to technical and non-technical stakeholders
- Mentor junior engineers and contribute to engineering best practices across the ML team

## Required Skills
- 5+ years of experience in machine learning engineering or applied ML roles
- Proven experience building and shipping production ML systems serving real-time predictions
- Strong Python skills and deep experience with modern ML frameworks (PyTorch, TensorFlow, or JAX)
- Experience designing end-to-end ML systems (data pipelines, training, deployment, monitoring, retraining)
- Strong understanding of model evaluation, failure analysis, and performance tuning in production systems
- Experience working with ambiguous prediction problems and translating them into model architectures and evaluation strategies
- Ability to explain complex ML systems and behavior to non-ML stakeholders (e.g., customers or product teams)
- Strong foundation in data structures, algorithms, and software engineering principles

## Nice to Have
- Background in energy, climate, utilities, or other regulated infrastructure domains
- Experience with time-series forecasting and probabilistic modeling
- Knowledge of conformal prediction, quantile regression, Bayesian methods, or uncertainty estimation
- Experience with MLOps tools and practices (model monitoring, retraining automation, evaluation systems)
- Contributions to open-source ML or time-series libraries (e.g., Darts, sktime, GluonTS, Nixtla)
- Experience working directly with customers and incorporating feedback into model improvements
- Familiarity with distributed systems and cloud platforms (AWS, GCP, or Azure)
- Experience in early-stage startups or small, high-ownership engineering teams

# About Fernway
Fernway is a Series A climate tech company helping utilities decarbonize faster by forecasting grid demand and renewable energy supply with AI.
We build time-series and probabilistic machine learning systems that predict electricity demand, solar/wind generation, and grid stress hours to days in advance — enabling utilities to dispatch cleaner energy with confidence.
We are a small, senior engineering team (5 engineers + CTO + PM) working closely with utility customers in production environments. Our models directly influence real-world energy decisions and renewable energy dispatch.

## Why This Role
- Your work directly impacts renewable energy adoption and grid decarbonization
- You’ll work on real ML systems used in production by utility customers
- Small, high-ownership team with direct access to the CTO and founders
- Strong focus on technical depth, model quality, and real-world impact
- Opportunity to grow into Staff-level leadership as the team scales
- Complex, meaningful ML problems (not LLM wrappers or surface-level AI features)
`,
    // Company Information
    companyName: 'Fernway',
    companyWebsite: 'https://fernway.ai',
    companyStage: 'Series A',
    companyMission: 'Helping utilities decarbonize faster by forecasting grid demand and renewable supply with AI',
    companyCulture: 'Mission-driven, technically rigorous, low-ego, and highly collaborative. Teams document decisions, share work early, and stay close to the utility customers using the product.',
    employeeCount: 42,
    companyIntro: 'Fernway is a fictional Series A climate technology company building forecasting software for electric utilities across North America.',
    productDescription: 'A forecasting and decision-support platform that helps utilities predict electricity demand, renewable generation, and grid stress so operators can dispatch cleaner energy reliably.',
    companyLinkedIn: 'https://www.linkedin.com/company/fernway-energy/',
    companyTwitter: 'https://x.com/fernway_energy',
    companyFacebook: 'https://www.facebook.com/fernwayenergy',
    companyInstagram: 'https://www.instagram.com/fernway_energy/',
    companyContext: 'Series A climate tech company with strong product-market fit. We have a launch utility customer in production and two more signing this quarter. Well-funded by climate-focused investors with a long runway.',
    teamFocus: 'Developing time-series and probabilistic ML models that predict electricity demand, solar/wind generation, and grid stress hours-to-days in advance — directly enabling utilities to dispatch cleaner energy with confidence',
    hiringTeam: 'addNewTeam',
    newTeamName: 'AI Engineering',
    newTeamDescription: 'Engineers',
    newTeamSize: 'Small (< 5)',
    teamSize: 5,
    teamStructure: '5 engineers (3 senior ML engineers, 2 mid-level engineers) and 1 PM, reporting to the CTO. Closely partnered with 3-person customer engineering team and applied research lead.',
    reportingTo: 'Dr. Maya Chen, CTO',

    // Position Definition
    jobTitle: 'AI Engineer Example',
    externalReference: 'ATS-FERN-AI-042',
    alternativeJobTitles: 'Senior ML Engineer, Staff ML Engineer, Applied Scientist (Forecasting)',
    roleType: 'newRole',
    urgency: 'medium',
    experienceLevel: 'Senior Level (5+ years)',
    
    businessNeed: "Fernway's launch utility customer has expanded their contract, and two new utility partners are signing in Q3. Our founding ML team is at capacity maintaining the production forecasting model, so we need a senior AI engineer to own architecture work on the next-generation model and lead the technical rollout for new customers.",
    
    keyOwnershipAreas:
      'Own the model architecture and training pipeline for Fernway\'s electricity demand and renewable generation forecasts, Lead technical rollout for new utility customers — partnering with customer engineering team on data integration, model tuning, and monitoring, Drive experimentation on probabilistic forecasting methods (quantile regression, conformal prediction) to improve uncertainty estimates, Contribute to MLOps stack: training pipelines, evaluation harnesses, and production monitoring',

    projectsInitiatives:
      'Building the next-gen forecasting model focused on extreme weather events (heat domes, winter storms) where current industry models break down, Partnering with a West Coast utility on day-ahead solar forecasting to support a 40% renewable dispatch target, Publishing benchmark results and contributing to open-source time-series ML tooling used by other grid operators',

    first3Months: 'Weeks 1–3: Onboarding, shadowing customer calls, and ramping on the existing model codebase. Weeks 4–6: Take ownership of one production model component with mentorship from founding ML engineer. Weeks 7–12: Scope, implement, and ship one measurable improvement to the production model end-to-end.',
    
    success12Months: 'Owns the architecture of Fernway\'s forecasting model and is the technical lead for at least two utility customers. Has shipped at least one major model improvement with measurable accuracy gain (e.g., reduced MAPE on peak demand hours). Is a credible technical voice in customer conversations. Mentors newer ML engineers as the team grows from 5 to 8.',

    // Candidate Profile
    requiredSkills: 
      'Has built and shipped at least one production ML model serving real-time predictions, Can take ambiguous forecasting problems and propose model architecture, evaluation plan, and rollout approach, Owned a production model end-to-end: training pipeline, deployment, monitoring, retraining, Strong Python skills and fluency in modern deep learning framework (PyTorch, JAX, or TensorFlow), Can explain model behavior and failure modes to non-ML technical audience',

    niceToHaveSkills:
      'Background in energy, climate, power systems, or regulated technical domain, Experience with probabilistic forecasting (quantile regression, conformal prediction, Bayesian methods), Open-source contributions to time-series ML libraries (Darts, sktime, GluonTS, Nixtla), Comfort joining customer calls and turning feedback into model improvements, Experience building MLOps tooling from scratch in early-stage environment',

    educationalBackground: 'BS in CS, EE, applied math, statistics, physics, or related field — or equivalent demonstrated experience shipping ML systems. We care more about evidence of shipping than credentials.',
    
    previousEnvironment: 'Early-stage startup experience (Seed–Series B), or a small product team within a larger company. Bonus if you\'ve shipped into regulated enterprise industry (utilities, healthcare, finance, public sector).',

    languageRequirements: 'Python (advanced) — required. Go or TypeScript helpful for contributions to backend and customer-facing tooling, but not required at hire. English fluency required for customer conversations.',

    // Methodologies & Culture
    teamMethodologies: 'Lightweight scrum — 2-week sprints, one weekly team planning, async daily updates in Slack. Strong norms around written design docs before building. Optimize for shipping over process.',

    reportsTo: 'Dr. Maya Chen',
    reportsToTitle: 'CTO',
    reportsToLinkedIn: 'https://www.linkedin.com/in/maya-chen-fernway/',
    
    teamCulture: 'Highly collaborative and customer-facing — most engineers join 1–2 customer calls per month. Weekly cross-team demo where ML engineers, customer engineers, and PM share progress. Low ego, high written-communication standards. Disagree openly in design reviews and align quickly.',

    // Work Arrangement
    workSchedule: 'Core hours 10am–3pm PT for overlap. Outside that, work when you\'re most productive. Generous PTO and actively encouraged to take it.',
    workArrangement: 'hybrid',
    officeLocation: 'San Francisco, California',
    preferredDays: 'Tuesday, Wednesday, Thursday',
    preferredTimezones: 'Pacific primary. Occasional early calls (8am PT) to support East Coast and Midwest utility customers.',
    toolsAndResources: 'MacBook Pro (M-series), Claude, Cursor, GitHub Copilot, Linear, Slack, Notion, AWS, Weights & Biases, Databricks',

    // Value Proposition
    companySellingPoint:
      'Mission with teeth: every model improvement directly enables more renewable energy on the grid. Point to MWh of clean energy your work helped dispatch., Real ML problems, not LLM wrappers: time-series forecasting under real-world constraints (weather, regulation, grid physics), with measurable accuracy targets, Small, senior team: 5 engineers, a CTO who codes, direct access to founders, chance to shape technical direction as you scale to 8+, Real customers, real revenue: launch utility customer in production and two more signing this quarter. Past "will this work" phase into "how good can we make it", Series A traction with long runway: well-funded by climate-focused investors, no pressure to chase trends',

    growthPossibilities: 'Clear path from Senior to Staff AI Engineer within 18–24 months for someone who delivers on model architecture work and grows into technical leader. As team scales from 5 to 15+ engineers over next two years, positioned to lead a sub-team. Conference attendance and publication support — we want our engineers visible in time-series ML and energy ML communities.',

    companyGoalsContribution: 'This role directly drives Fernway\'s two most important business metrics: forecast accuracy (determines whether utilities renew and expand) and time-to-launch for new customers (determines growth rate). Measurable improvement in peak-hour forecast error translates to meaningful renewable energy dispatched and serves as basis for case studies that win next tier of customers.',

    professionalDevelopmentBudget: 'Yes',

    // Salary & Benefits
    salaryRange: '$220,000 – $265,000 base',
    bonusEquityAvailable: 'Yes',
    eligibleForRelocationAllowance: 'Yes',
    benefitsPackage:
      'Unlimited PTO with 3-week minimum encouraged, 100% employer-paid medical, dental, and vision premiums for employees (90% for dependents), 401(k) with 4% match, 16 weeks paid parental leave for all parents, $1,000/year wellness stipend, Home office stipend of $1,500 at hire, Catered lunch Tuesday–Thursday (in-office days), Free EV charging in office garage and $5,000 EV purchase incentive after one year, Annual offsite (recent: Joshua Tree), High-quality espresso machine',
    roleSpecificPerks:
      'Quarterly utility-site visits, an annual climate-tech conference budget, paid open-source contribution time, and access to anonymized grid datasets for approved research projects.',

    // Collaboration Agreements
    mainCommunicationChannel: 'Dedicated Slack channel (#hiring-ai-eng-forecasting) for all real-time coordination — candidate updates, scheduling, debriefs. ATS is the source of truth for written feedback and stage tracking. Weekly 30-minute hiring sync between Maya (CTO/hiring manager), the recruiter, and the team admin to review pipeline, unblock candidates, and recalibrate if needed.',
    feedbackAgreements: 'Interviewers submit structured feedback in the ATS within 24 hours of every interview, using our standard rubric (technical depth, communication, ownership, mission fit). For technical interviews, interviewers paste their raw notes plus a clear hire/no-hire recommendation with reasoning. No discussing candidates outside the ATS before submitting written feedback — we want independent assessments before the debrief.',
    teamTimeCommitment: 'Yes',
    interviewSchedulingRestrictions: 'Friday afternoons (team focus block, no meetings). 12–1pm PT daily (lunch). Tuesdays 10–11am PT (engineering-wide standup). Avoid scheduling more than 2 onsite loops per interviewer per week.',
    interviewCoordinator: 'Priya Desai, Talent Operations (priya@fernway.ai). Backed up by the recruiter for first-round scheduling.',
    roleClosingMethod: 'cohort_comparison',
    impactModelInputs: {
      initialScreenCandidates: 20,
      comparableRoleStages: 6,
      plannedHires: 1,
      desiredFinalists: 3,
    },
    impactModelCompletedAt: '2026-04-26T12:00:00.000Z',

    // Interview Structure
    numInterviewers: 5,
    avgInterviewHours: 3,
    interviewApproach: 'Comprehensive technical and culture-fit evaluation over 4 rounds, designed to assess both deep technical expertise and team alignment. We prioritize practical problem-solving and communication skills.',
    interviewPlan: {
      type: 'yogen',
      stages: [
        {
          id: 'stage-1',
          name: 'Recruiter Screen',
          stageFocusArea: [
            'initial-screening',
            'cultural-fit'
          ],
          goal: "Confirm the candidate meets the must-have qualifications (production ML model ownership, Python fluency, startup environment fit), gauge genuine interest in climate tech and the forecasting problem space, align on compensation expectations early, and answer their questions about Fernway. By the end of this call we should know whether to invest a hiring manager's time in round two.",
          type: 'phone',
          duration: 30,
          useAiTools: false,
          interviewers: [
            {
              name: 'Jordan Avery',
              linkedIn: 'https://www.linkedin.com/in/jordan-avery-fernway/',
              focusArea: 'initial-screening',
              trainedByYogen: true,
              isBackup: false,
            },
            {
              name: 'Taylor Brooks',
              linkedIn: 'https://www.linkedin.com/in/taylor-brooks-fernway/',
              focusArea: 'initial-screening',
              trainedByYogen: true,
              isBackup: true,
            }
          ],
          daysToNext: 3,
          cost: 1000,
        },
        {
          id: 'stage-2',
          name: 'HM Interview',
          stageFocusArea: [
            'technical-interview'
          ],
          goal: 'Evaluate technical depth, ML system design experience, and mission fit',
          type: 'video',
          duration: 45,
          useAiTools: false,
          interviewers: [
            {
              name: 'Dr Maya Chen',
              linkedIn: 'https://www.linkedin.com/in/maya-chen-fernway/',
              focusArea: 'technical-interview',
              trainedByYogen: true,
              isBackup: false,
            },
            {
              name: 'Omar Rahman',
              linkedIn: 'https://www.linkedin.com/in/omar-rahman-fernway/',
              focusArea: 'technical-interview',
              trainedByYogen: true,
              isBackup: true,
            }
          ],
          daysToNext: 2,
          cost: 2500,
        },
        {
          id: 'stage-3',
          name: 'Technical Take-Home',
          stageFocusArea: [
            'technical-interview'
          ],
          goal: 'Time-series modeling exercise on synthetic grid data',
          type: 'onsite',
          duration: 180,
          useAiTools: true,
          interviewers: [
            {
              name: 'Casey Park',
              linkedIn: 'https://www.linkedin.com/in/casey-park-fernway/',
              focusArea: 'technical-interview',
              trainedByYogen: true,
              isBackup: false,
            },
            {
              name: 'Priya Nair',
              linkedIn: 'https://www.linkedin.com/in/priya-nair-fernway/',
              focusArea: 'technical-interview',
              trainedByYogen: true,
              isBackup: true,
            }
          ],
          daysToNext: 5,
          cost: 2500,
          warnings: [],
        },
        {
          id: 'stage-4',
          name: 'Onsite Loop',
          stageFocusArea: [
            'behavioral',
            'leadership',
            'communication',
            'executive'
          ],
          goal: 'Architecture deep-dive, coding, take-home review, cross-functional collaboration',
          type: 'onsite',
          duration: 240,
          useAiTools: false,
          interviewers: [
            {
              name: 'Lena Ortiz',
              linkedIn: 'https://www.linkedin.com/in/lena-ortiz-fernway/',
              focusArea: 'leadership',
              trainedByYogen: true,
              isBackup: false,
            },
            {
              name: 'Theo Lindqvist',
              linkedIn: 'https://www.linkedin.com/in/theo-lindqvist-fernway/',
              focusArea: 'communication',
              trainedByYogen: true,
              isBackup: false,
            },
            {
              name: 'Anya Volkov',
              linkedIn: 'https://www.linkedin.com/in/anya-volkov-fernway/',
              focusArea: 'executive',
              trainedByYogen: true,
              isBackup: false,
            },
            {
              name: 'Markus Olawale',
              linkedIn: 'https://www.linkedin.com/in/marcus-olawale-fernway/',
              focusArea: 'behavioral',
              trainedByYogen: true,
              isBackup: false,
            },
            {
              name: 'Morgan Lee',
              linkedIn: 'https://www.linkedin.com/in/morgan-lee-fernway/',
              focusArea: 'behavioral',
              trainedByYogen: true,
              isBackup: true,
            }
          ],
          daysToNext: 3,
          cost: 2000,
          warnings: [],
        },
      ],
      metrics: {
        totalProcessTime: '28 days',
        interviewCost: 8000,
        engineeringCost: 6000,
        candidateTimeInvestment: '240 minutes',
        candidateSentiment: 'High',
      },
    },

    hiringChallenges: [
      'Finding ML engineers with production forecasting experience (most have LLM/NLP background)',
      'Identifying candidates comfortable working directly with utility customers and explaining model behavior',
      'Sourcing engineers who understand time-series ML and probabilistic forecasting methods',
      'Balancing research-oriented candidates with those who can ship production systems on schedule',
    ],

    customizedQuestions: {
      stages: [
        {
          stageName: 'HM Interview',
          description: 'Technical ownership and mission alignment',
          questions: [
            {
              question: 'Tell us about a production ML system you owned from model design through monitoring. What tradeoffs did you make?',
              evaluation: 'Looks for genuine end-to-end ownership, specific technical decisions, measurable outcomes, and thoughtful discussion of reliability.',
            },
          ],
        },
        {
          stageName: 'Onsite Loop',
          description: 'Architecture, collaboration, and communication',
          questions: [
            {
              question: 'How would you explain a forecast failure during an extreme-weather event to a utility operations leader?',
              evaluation: 'Looks for clarity, accountability, appropriate uncertainty, customer empathy, and a practical mitigation plan.',
            },
          ],
        },
      ],
    },
    rolePlanFeatures: {
      'decision-matrix': {
        version: 1,
        savedAt: '2026-04-26T12:00:00.000Z',
        savedBy: 'example-user',
        savedByName: 'Priya Desai',
        inputs: { decisionMethod: 'cohort_comparison' },
        output: {
          title: 'Hiring decision matrix',
          paragraphs: [
            'Advance candidates who meet the production-ML ownership threshold and demonstrate clear technical communication. Finalists are compared on forecasting depth, system reliability judgment, customer empathy, and evidence of delivering measurable outcomes.',
          ],
          candidateParagraphs: [
            'We evaluate every candidate against the same role requirements, with emphasis on production ML ownership, technical judgment, and clear communication. Finalists are considered together after completing the planned interviews.',
          ],
        },
      },
      'interview-architect': {
        version: 1,
        savedAt: '2026-04-26T12:00:00.000Z',
        savedBy: 'example-user',
        savedByName: 'Priya Desai',
        inputs: { format: 'forecasting-system-design' },
        output: {
          title: 'Technical interview approach',
          paragraphs: [
            'Use a realistic forecasting scenario with synthetic grid data. Assess problem framing, evaluation design, uncertainty handling, deployment thinking, and the candidate’s ability to explain tradeoffs without relying on domain trivia.',
          ],
          candidateParagraphs: [
            'The technical portion uses a realistic forecasting scenario and synthetic data. We care about how you frame the problem, evaluate alternatives, reason about uncertainty, and communicate tradeoffs.',
          ],
        },
      },
      'question-checker': {
        version: 1,
        savedAt: '2026-04-26T12:00:00.000Z',
        savedBy: 'example-user',
        savedByName: 'Priya Desai',
        inputs: { reviewedQuestion: 'production-ml-ownership' },
        output: {
          title: 'Reviewed interview question',
          paragraphs: [
            'Approved question: Describe a production ML system you owned end to end. Use consistent follow-up prompts about monitoring, failures, and measurable outcomes; avoid assumptions about employer prestige or access to unusually large infrastructure.',
          ],
        },
      },
    },

    // Pre-populated LinkedIn Boolean Search for example
    linkedInBooleanLogic: {
      primarySearch: '(("machine learning" OR "ML engineer" OR "AI engineer") AND ("time series" OR "forecasting" OR "prediction" OR "timeseries") AND (Python OR PyTorch OR TensorFlow OR JAX) AND ("production" OR "production systems" OR "production ML" OR "shipped"))',
      secondarySearch: '(("senior machine learning" OR "senior ML" OR "ML architect") AND (energy OR utilities OR grid OR power OR renewable OR climate) AND ("end-to-end" OR "full stack" OR "model deployment" OR "MLOps"))',
      requiredSkills: ['Python', 'Machine Learning', 'Time-series Forecasting', 'PyTorch or TensorFlow', 'Production Systems'],
      optionalSkills: ['Probabilistic Forecasting', 'Energy/Utilities Domain', 'MLOps', 'Distributed Systems'],
    },

    // Pre-populated Checker Results for Risk & Warning Summary
    checkerResults: {
      responsibilities: {
        summary: 'Responsibilities are well-defined with clear ownership areas and measurable outcomes. Good balance between technical depth and cross-functional collaboration.',
        riskLevel: 'low',
        risks: [],
        warnings: [
          'Customer call participation burden (1-2 calls/month) may impact deep technical work capacity - consider rotation schedule',
          'Model architecture ownership while maintaining existing system may create bottleneck - define escalation path'
        ],
        suggestions: [
          'Define a "no-meeting" block (e.g., Wednesdays 2-5pm) to protect deep work time',
          'Create a backup person for critical production issues to reduce single-point-of-failure risk',
          'Document decision-making framework for model improvements to enable faster delegation'
        ],
        metrics: { scopeFit: 0.9, timelineRisk: 0.6, clarity: 0.95 }
      },
      requirements: {
        summary: 'Requirements are specific and well-aligned with role needs. Good mix of required technical skills and nice-to-haves. Production ML shipping experience is appropriately emphasized.',
        riskLevel: 'medium',
        risks: [],
        warnings: [
          'Probabilistic forecasting (quantile regression, conformal prediction) is specialized - limited talent pool',
          'Time-series forecasting focus narrows candidate pool significantly - consider how to message to broader ML audience'
        ],
        suggestions: [
          'Create interview questions that help identify forecasting potential even if candidates haven\'t done time-series before',
          'Emphasize transferable skills from adjacent domains (demand prediction, anomaly detection, inventory forecasting)',
          'Consider adding "comfort with learning new ML domains quickly" as an implicit requirement'
        ],
        metrics: { clarity: 0.92, alignment: 0.88, exclusionRisk: 0.7 }
      },
      candidate: {
        summary: 'Candidate profile is clear but quite specialized. Well-balanced technical depth with communication requirements. May be challenging to find someone with all criteria.',
        riskLevel: 'medium',
        risks: [],
        warnings: [
          'Overlap between "production ML shipping" and "probabilistic forecasting" creates very narrow candidate profile',
          'Customer-facing expectation for "explaining model behavior" requires communication skills not always found in deep ML engineers'
        ],
        suggestions: [
          'Consider two-tier criteria: must-haves (production ML shipping) and nice-to-haves (forecasting background)',
          'Look for candidates with strong technical communication in GitHub/Medium/talks rather than prior customer-facing work',
          'Add communication assessment to technical interview to evaluate ability to explain complex ML'
        ],
        metrics: { fitScore: 0.75, consistencyScore: 0.85, attractivenessScore: 0.8 }
      },
      selling: {
        summary: 'Value proposition is compelling with strong mission focus and real customer traction. Good balance of growth opportunity and interesting technical problems.',
        riskLevel: 'low',
        risks: [],
        warnings: [
          'Series A funding status and "long runway" claim should be substantiated during recruiting (investors will ask)',
          '"Real customers in production" is great but limited track record (one launch customer) - this may feel risky to some candidates'
        ],
        suggestions: [
          'Emphasize customer success metrics (utilities renewing/expanding) as proof of product-market fit',
          'Highlight the 2 customers "signing this quarter" in job posting and interviews once they\'re public',
          'Prepare specific examples of customer feedback leading to model improvements'
        ],
        metrics: { compellingness: 0.88, clarity: 0.92 }
      },
      interview: {
        summary: 'Interview plan is well-structured with 4 clear stages evaluating different dimensions. Good progression from screening to culture fit. Right balance of technical depth and team assessment.',
        riskLevel: 'low',
        risks: [],
        warnings: [
          'Two 75-minute technical interviews (stages 2 & 3) may feel redundant to candidates - consider consolidation',
          'No async technical assignment - relying entirely on live interviews may disadvantage candidates who interview poorly under pressure'
        ],
        suggestions: [
          'Consider adding an optional pre-interview take-home (e.g., "analyze this time-series problem") for candidates to showcase work',
          'Combine Technical Deep Dive and Systems Design into single 90-minute session to respect candidate time',
          'Add interview rubric and share with candidates so they understand what you\'re evaluating',
          'Ensure 24-hour feedback submission to maintain momentum and prevent groupthink before debrief'
        ],
        metrics: { structureQuality: 0.88, biasRisk: 0.65, signalQuality: 0.85 }
      }
    },
  },
}

exampleAIEngineerPosition.positionData.impactModelSnapshot = createImpactModelSnapshotV1(
  {
    ...exampleAIEngineerPosition.positionData.impactModelInputs,
    decisionMethod: exampleAIEngineerPosition.positionData.roleClosingMethod,
  } as ImpactModelInputsV1,
  exampleAIEngineerPosition.positionData.interviewPlan.stages as ImpactModelStageV1[],
  '2026-04-26T12:00:00.000Z',
)
exampleAIEngineerPosition.positionData.impactModelSnapshot.result.stages.forEach(
  (stageResult: { plannedHiringTeamCost: number }, index: number) => {
    exampleAIEngineerPosition.positionData.interviewPlan.stages[index].cost =
      Math.round(stageResult.plannedHiringTeamCost / 100) * 100
  },
)

export { exampleAIEngineerPosition }
